/**
 * Survey API endpoints for fetching survey data.
 */

import { apiClient } from './client';
import type { SurveyData } from '@/types';

/**
 * Response from survey data endpoint (included with session start).
 */
export interface SurveyDataResponse {
  survey: SurveyData['survey'];
  questions: SurveyData['questions'];
  logic: SurveyData['logic'];
  pages: SurveyData['pages'];
  versionId: string;
  // Phase 78 — locale metadata from the player content endpoint. Optional so
  // the session-start-bundled path (which omits them) still type-checks.
  resolvedLocale?: string;
  defaultLocale?: string;
  availableLocales?: string[];
  isRtl?: boolean;
}

/**
 * Get survey data for the player.
 * This is typically bundled with the session start response,
 * but can be fetched separately if needed.
 */
export async function getSurveyData(
  surveyId: string,
  sessionId: string,
  locale?: string
): Promise<SurveyDataResponse> {
  const response = await apiClient.get<SurveyDataResponse>(
    `/api/v1/player/surveys/${surveyId}`,
    {
      // Strict locale match happens server-side; an unknown/omitted locale
      // resolves to the survey default.
      params: { sessionId, ...(locale ? { locale } : {}) },
    }
  );
  return response.data;
}

/**
 * Check quota availability for a session.
 */
export async function checkQuota(
  surveyId: string,
  sessionId: string,
  quotaCellId: string
): Promise<{ available: boolean; remaining: number }> {
  const response = await apiClient.get<{
    available: boolean;
    remaining: number;
  }>(`/api/v1/player/surveys/${surveyId}/quota/${quotaCellId}`, {
    params: { sessionId },
  });
  return response.data;
}
